import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Contract {
  id: string;
  name: string;
  type: string | null;
  contractType: string | null;
  value: string | null;
  status: string;
  startDate: string;
  endDate: string;
  account?: { id: string; name: string };
}

interface ContractsResponse {
  contracts: Contract[];
  total: number;
}

export default function ContractsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading } = useQuery<ContractsResponse>({
    queryKey: ["/api/contracts", { search: searchValue, page: currentPage, limit: pageSize }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(pageSize),
        ...(searchValue && { search: searchValue }),
      });
      return api.get<ContractsResponse>(`/contracts?${params}`, sessionId);
    },
  });

  const columns = [
    {
      key: "name",
      header: "Contract Name",
      sortable: true,
      cell: (row: Contract) => <div className="font-medium">{row.name}</div>,
    },
    {
      key: "account",
      header: "Account",
      cell: (row: Contract) => <span>{row.account?.name || "-"}</span>,
    },
    {
      key: "type",
      header: "Type",
      cell: (row: Contract) => (
        <span className="text-muted-foreground capitalize">
          {row.type?.replace(/_/g, " ") || "-"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row: Contract) => <StatusBadge status={row.status} type="account" />,
    },
    {
      key: "value",
      header: "Value",
      className: "text-right",
      cell: (row: Contract) => (
        <span className="font-medium">
          {row.value ? `$${Number(row.value).toLocaleString()}` : "-"}
        </span>
      ),
    },
    {
      key: "endDate",
      header: "End Date",
      cell: (row: Contract) => (
        <span className="text-muted-foreground">
          {row.endDate ? new Date(row.endDate).toLocaleDateString() : "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contracts"
        actions={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search contracts..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-64"
            />
            <Button size="sm" onClick={() => setLocation("/contracts/new")} data-testid="button-new-contract">
              <Plus className="h-4 w-4 mr-1" />
              New Contract
            </Button>
          </div>
        }
      />

      <DataTable
        data={data?.contracts || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/contracts/${row.id}`)}
        emptyMessage="No contracts found"
        emptyIcon={<ClipboardList className="h-12 w-12 text-muted-foreground/50" />}
        pageSize={pageSize}
        totalCount={data?.total || 0}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
